/* eslint-disable @typescript-eslint/no-empty-function */
/* eslint-disable @typescript-eslint/no-explicit-any */
'use client'

import defaultLocale from '@/stores/defaultLocale'
import getLocale from '@/stores/locales'
import { useRouter } from 'next/navigation'
import { Suspense, useEffect } from 'react'
import { ProductProps } from './types/services/getProduct'

import { UseLoggedContext } from '@mercadoni/sinister/dist/cjs/authentication'
import { useHeaderDataContext } from '@mercadoni/sinister/dist/cjs/shared/contexts'
import { UseListContext } from '@mercadoni/sinister/dist/cjs/shoppingLists'
import useCartV3 from '@mercadoni/sinister/dist/cjs/useCartV3'

import {
  ProductDetail,
  ProductDetailSkeleton,
  ProductProvider,
  getProductListInfo,
} from '@mercadoni/sinister/dist/cjs/productv2'

import { hasRestrictedPdpValidation } from '@/components/RestrictedAge/useCases/pdpValidation'
import { useReplacementsContext } from '@/context/ReplacementsContext'
import { useRestrictedAgeContext } from '@/context/RestrictedAgeContext'
import useGuessAction from '@/hooks/useGuessAction'
import parseForHybrid from '@/navigation/CSR/goToCategory/parseForHybrid'
import gotToPromoTag from '@/navigation/CSR/gotToPromoTag'
import productPath from '@/utils/productPath'
import {
  useInteraction,
  useProductItemEvent,
  useViewItemList,
} from '@mercadoni/sinister/dist/cjs/monitors'
import Link from 'next/link'
import { ProductCatalog } from '~types/index'
import { UrlViewStyles } from '../../app/(ecommerce)/p/components/styles'

const ProductDetailCSR = ({ product, clientModel }: ProductProps) => {
  const copies = getLocale() || defaultLocale
  const router = useRouter()
  const { sendViewProductEvent, sendSelectProductEvent } = useProductItemEvent()
  const { favoriteService } = UseListContext()
  const { headerData } = useHeaderDataContext()
  const { isLogged } = UseLoggedContext()

  const { sendInteractionEvent } = useInteraction()
  const onGuessAction = useGuessAction({ router, forceToLogin: false })
  const { sendViewItemListEvent } = useViewItemList()

  const { openRestrictedAge, getStoredRestrictedAge, onBeforeAddProduct } =
    useRestrictedAgeContext()

  const { enhanceImg } = clientModel
  const { onAddProduct } = useReplacementsContext()
  const brandUrl = (brand: string) => {
    const brandParams = new URLSearchParams({
      name: brand,
      brands: brand,
    })
    return `/search?${brandParams.toString()}`
  }

  const CrumbLink = ({ href, ...props }: { href: string }) => {
    const redirectHref = href === '/' ? href : parseForHybrid({ slug: href })
    return <Link href={redirectHref} {...props} />
  }

  const BrandLink = ({ href, ...props }: { href: string }) => (
    <Link href={brandUrl(href)} {...props} />
  )

  const onClickPromoTag = gotToPromoTag(router)

  const onClickRelated = (product: ProductCatalog) => () => {
    sendSelectProductEvent({
      trackSource: 'related',
      product,
    })
    router.push(productPath(product))
  }

  const onClickSuggestion = (product: ProductCatalog) => () => {
    sendSelectProductEvent({
      trackSource: 'suggested',
      product,
    })
    router.push(productPath(product))
  }

  const onVariantSelection = (product: ProductCatalog) => {
    sendSelectProductEvent({
      trackSource: 'variants',
      product,
    })
    router.push(productPath(product))
  }

  const listInfo = {
    screen_name: headerData?.screenName,
    ...getProductListInfo(headerData?.screenName),
  }

  const onViewItemListEvent = (params: any) => {
    sendViewItemListEvent({
      ...params,
      customParams: {
        screen_name: headerData?.screenName,
        url: window.location.href,
      },
    })
  }

  const onViewItem = (params: any) => {
    sendViewProductEvent({
      ...params,
      listInfo,
    })
  }

  const onClickAddProduct = () => {
    onAddProduct({ product })
  }

  useEffect(() => {
    const hasRestrictedPdp = hasRestrictedPdpValidation(product)
    if (hasRestrictedPdp && getStoredRestrictedAge() !== true) {
      openRestrictedAge()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <UrlViewStyles
      className="url-view"
      hasFormat={product?.formats?.length > 0}
    >
      <ProductProvider
        baseProduct={product}
        setVariantId={() => {}}
        enhanceImg={enhanceImg}
        consumer="productDetailUrl"
        clientConfig={clientModel}
        favoriteService={favoriteService}
        fallback={
          <ProductDetailSkeleton
            version={clientModel.product?.layouts?.productDetail || 'B'}
          />
        }
        copies={copies}
        onInteraction={sendInteractionEvent}
        listInfo={listInfo}
        isLogged={isLogged}
        CrumbLink={CrumbLink}
        BrandLink={BrandLink}
        onClickPromoTag={onClickPromoTag}
        onClickRelated={onClickRelated}
        onClickSuggestion={onClickSuggestion}
        onClickAddProduct={onClickAddProduct}
        onCarlessProduct={onGuessAction}
        onViewItemListEvent={onViewItemListEvent}
        onVariantSelection={onVariantSelection}
        LinkComponent={({ href, ...props }: { href: string }) => (
          <a href={href} {...props} />
        )}
        useCart={useCartV3}
        onBeforeAddProduct={onBeforeAddProduct}
      >
        <Suspense fallback="">
          <ProductDetail onViewItem={onViewItem} exclusiveEvent />
        </Suspense>
      </ProductProvider>
    </UrlViewStyles>
  )
}
export default ProductDetailCSR
